import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import api from '../services/api';
import toast from 'react-hot-toast';
import { PlusIcon, PencilSquareIcon, XMarkIcon, PaperAirplaneIcon } from '@heroicons/react/24/outline';

const style = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
  .at*{box-sizing:border-box;}
  .at{font-family:'Sora',sans-serif;color:#1a1d2e;animation:atFade .4s ease both;}
  @keyframes atFade{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}}
  .at-header{display:flex;align-items:flex-start;justify-content:space-between;margin-bottom:1.5rem;flex-wrap:wrap;gap:1rem;}
  .at-title{font-size:1.35rem;font-weight:700;letter-spacing:-.02em;color:#0f1123;}
  .at-sub{font-size:.76rem;color:#9094a8;margin-top:3px;}
  .req-banner{background:#fff7ed;border:1px solid #fed7aa;border-radius:10px;padding:10px 14px;font-size:.76rem;color:#c2410c;margin-bottom:1.4rem;display:flex;align-items:center;gap:8px;}
  .btn{display:inline-flex;align-items:center;gap:7px;padding:8px 14px;border:none;border-radius:9px;font-family:'Sora',sans-serif;font-size:.78rem;font-weight:600;cursor:pointer;transition:all .18s;}
  .btn svg{width:14px;height:14px;stroke-width:2;}
  .btn-orange{background:#f97316;color:white;} .btn-orange:hover{box-shadow:0 4px 14px rgba(249,115,22,.3);}
  .btn-red{background:#fef2f2;color:#ef4444;border:1px solid #fecaca;} .btn-red:hover{background:#ef4444;color:white;}
  .btn-ghost{background:#f0f2f7;color:#6b7194;} .btn-ghost:hover{background:#e4e7f0;}
  .btn-sm{padding:5px 10px;font-size:.72rem;} .btn:disabled{opacity:.5;cursor:not-allowed;}
  .info-box{background:#fff7ed;border:1px solid #fed7aa;border-radius:12px;padding:12px 16px;margin-bottom:1.4rem;font-size:.78rem;color:#92400e;line-height:1.6;}
  .info-box strong{font-weight:700;}
  .types-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(240px,1fr));gap:1rem;margin-bottom:1.4rem;}
  .type-card{background:white;border-radius:14px;border:1px solid #e8ebf4;padding:1rem 1.1rem;display:flex;align-items:center;justify-content:space-between;gap:10px;transition:all .15s;}
  .type-card:hover{border-color:#f97316;box-shadow:0 4px 14px rgba(249,115,22,.08);}
  .type-card.inactive{opacity:.55;}
  .type-info{display:flex;align-items:center;gap:10px;}
  .type-order{width:26px;height:26px;border-radius:7px;background:#fff7ed;color:#f97316;display:flex;align-items:center;justify-content:center;font-size:.72rem;font-weight:700;font-family:'JetBrains Mono',monospace;flex-shrink:0;}
  .type-name{font-size:.86rem;font-weight:600;color:#0f1123;}
  .type-status{font-size:.67rem;color:#9094a8;margin-top:2px;}
  .type-actions{display:flex;gap:6px;flex-shrink:0;}
  .at-empty{text-align:center;padding:3rem 2rem;color:#9094a8;font-size:.82rem;}
  .at-loading{display:flex;align-items:center;justify-content:center;height:150px;gap:10px;color:#9094a8;}
  .spinner{width:18px;height:18px;border:2px solid #e4e7f0;border-top-color:#f97316;border-radius:50%;animation:spin .7s linear infinite;}
  @keyframes spin{to{transform:rotate(360deg);}}
  .modal-backdrop{position:fixed;inset:0;background:rgba(15,17,35,.55);backdrop-filter:blur(4px);display:flex;align-items:center;justify-content:center;z-index:1000;padding:20px;}
  .modal{background:white;border-radius:20px;padding:26px;width:100%;max-width:420px;max-height:90vh;overflow-y:auto;box-shadow:0 20px 60px rgba(0,0,0,.2);animation:modalIn .25s ease;}
  @keyframes modalIn{from{opacity:0;transform:scale(.94)}to{opacity:1;transform:scale(1)}}
  .modal-head{display:flex;align-items:center;justify-content:space-between;margin-bottom:18px;}
  .modal-title{font-size:1rem;font-weight:700;color:#0f1123;}
  .modal-close{width:28px;height:28px;border-radius:7px;border:none;background:#f0f2f7;cursor:pointer;display:flex;align-items:center;justify-content:center;}
  .modal-close svg{width:14px;height:14px;stroke-width:2;}
  .modal-btns{display:flex;gap:8px;justify-content:flex-end;margin-top:18px;flex-wrap:wrap;}
  .form-field{display:flex;flex-direction:column;gap:5px;margin-bottom:14px;}
  .form-label{font-size:.68rem;font-weight:700;color:#9094a8;text-transform:uppercase;letter-spacing:.06em;}
  .form-input,.form-select{font-family:'Sora',sans-serif;font-size:.82rem;padding:9px 11px;border:1.5px solid #e0e4f0;border-radius:9px;background:#f8f9fc;color:#1a1d2e;outline:none;width:100%;}
  .form-input:focus,.form-select:focus{border-color:#f97316;background:white;}
  @media(max-width:768px){
    .at-title{font-size:1.1rem;} .at-header{margin-bottom:1rem;}
    .types-grid{grid-template-columns:1fr;}
    .modal{padding:18px;} .modal-btns{justify-content:stretch;} .modal-btns .btn{flex:1;justify-content:center;}
  }
`;

const CoordAssessmentTypes = () => {
  const [types,   setTypes]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal,   setModal]   = useState(null); // null | {mode:'add'|'edit'|'toggle', type?}
  const [saving,  setSaving]  = useState(false);
  const [form,    setForm]    = useState({ name:'', order:'0' });
  const [note,    setNote]    = useState('');
  const [assessmentMode, setAssessmentMode] = useState('assessment');
  const isTermMode = assessmentMode === 'term';

  useEffect(() => {
    api.get('/coordinator/assessment-mode')
      .then(({ data }) => {
        const mode = data.assessmentMode || 'assessment';
        setAssessmentMode(mode);
        fetchTypes(mode);
      })
      .catch(() => fetchTypes('assessment'));
  }, []);

  const fetchTypes = async (mode = assessmentMode) => {
    const kindParam = mode === 'term' ? '?kind=term_component' : '';
    try {
      setLoading(true);
      // Coordinator can see all types (including inactive) for context
      const { data } = await api.get(`/coordinator/assessment-types-all${kindParam}`);
      setTypes(data);
    } catch {
      // Fallback to active-only endpoint
      try {
        const { data } = await api.get(`/coordinator/assessment-types${kindParam}`);
        setTypes(data);
      } catch { toast.error('Failed to load'); }
    } finally { setLoading(false); }
  };

  const openAdd = () => {
    setForm({ name:'', order: String(types.length) });
    setNote('');
    setModal({ mode:'add' });
  };
  const typeLabel = isTermMode ? 'Component' : 'Type';

  const openEdit = (t) => {
    setForm({ name: t.name, order: String(t.order) });
    setNote('');
    setModal({ mode:'edit', type: t });
  };

  const openToggle = (t) => { setNote(''); setModal({ mode:'toggle', type: t }); };

  const submit = async () => {
    if (!note.trim()) { toast.error('Reason is required'); return; }
    try {
      setSaving(true);
      const { mode, type } = modal;

      let payload = {};
      if (mode === 'add') {
        if (!form.name.trim()) { toast.error('Name is required'); return; }
        payload = { action: 'add', name: form.name.trim(), order: Number(form.order), note };
        if (isTermMode) payload.kind = 'term_component';
      } else if (mode === 'edit') {
        if (!form.name.trim()) { toast.error('Name is required'); return; }
        payload = { action: 'edit', typeId: type._id, name: form.name.trim(), order: Number(form.order), oldName: type.name, note };
      } else if (mode === 'toggle') {
        payload = { action: 'toggle', typeId: type._id, name: type.name, active: !type.active, note };
      }

      await api.post('/coordinator/requests', {
        type: 'assessment_type_action',
        payload,
      });
      toast.success('Request submitted — awaiting admin approval');
      setModal(null);
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setSaving(false); }
  };

  const getModalTitle = () => {
    if (!modal) return '';
    if (modal.mode === 'add')    return `Request: Add ${isTermMode ? 'Term Component' : 'Assessment Type'}`;
    if (modal.mode === 'edit')   return `Request: Edit "${modal.type?.name}"`;
    if (modal.mode === 'toggle') return `Request: ${modal.type?.active ? 'Deactivate' : 'Activate'} "${modal.type?.name}"`;
    return '';
  };

  return (
    <div className="at"><style>{style}</style>
      <div className="at-header">
        <div>
          <div className="at-title">{isTermMode ? 'Term Components' : 'Assessment Types'}</div>
          <div className="at-sub">
            {isTermMode
              ? 'View term components · submit requests to add, edit or deactivate'
              : 'View assessment types · submit requests to add, edit or deactivate'}
          </div>
        </div>
        <button className="btn btn-orange" onClick={openAdd}><PlusIcon />Add {typeLabel}</button>
      </div>

      <div className="req-banner">
        📋 All changes (add, edit, toggle active) require admin approval.
      </div>

      <div className="info-box">
        {isTermMode ? (
          <>
            <strong>How this works:</strong> Each component can be graded within any term.
            Teachers select from these components when entering marks for a term.
            Use <strong>Order</strong> to control the column sequence.
            Inactive components won't appear in the teacher dropdown.
          </>
        ) : (
          <>
            <strong>How this works:</strong> Each type becomes a column in grade reports.
            Teachers select from these types when creating assessments.
            Use <strong>Order</strong> to control the column sequence.
            Inactive types won't appear in the teacher dropdown.
          </>
        )}
      </div>

      {loading ? (
        <div className="at-loading"><div className="spinner"/>Loading…</div>
      ) : types.length === 0 ? (
        <div className="at-empty">
          {isTermMode
            ? 'No term components yet. Submit a request to add some.'
            : 'No assessment types yet. Submit a request to add some.'}
        </div>
      ) : (
        <div className="types-grid">
          {types.map(t => (
            <div key={t._id} className={`type-card ${!t.active ? 'inactive' : ''}`}>
              <div className="type-info">
                <div className="type-order">{t.order}</div>
                <div>
                  <div className="type-name">{t.name}</div>
                  <div className="type-status">{t.active ? '● Active' : '○ Inactive'}</div>
                </div>
              </div>
              <div className="type-actions">
                <button className="btn btn-ghost btn-sm" onClick={() => openToggle(t)} title={t.active ? 'Request deactivate' : 'Request activate'}>
                  {t.active ? '●' : '○'}
                </button>
                <button className="btn btn-ghost btn-sm" onClick={() => openEdit(t)}><PencilSquareIcon /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {modal && createPortal(
        <div className="modal-backdrop" onClick={() => setModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-head">
              <div className="modal-title">{getModalTitle()}</div>
              <button className="modal-close" onClick={() => setModal(null)}><XMarkIcon /></button>
            </div>

            {modal.mode === 'toggle' && (
              <div style={{textAlign:'center',padding:'1rem 0 .5rem'}}>
                <div style={{fontSize:'2rem',marginBottom:12}}>{modal.type?.active ? '🔴' : '🟢'}</div>
                <div style={{fontWeight:700,fontSize:'.92rem',color:'#0f1123',marginBottom:6}}>
                  {modal.type?.active ? 'Deactivate' : 'Activate'} "{modal.type?.name}"?
                </div>
                <div style={{fontSize:'.78rem',color:'#9094a8',marginBottom:14}}>
                  {modal.type?.active
                    ? 'Inactive types won\'t appear in teacher dropdown.'
                    : 'Active types appear in teacher dropdown when creating assessments.'}
                </div>
              </div>
            )}

            {(modal.mode === 'add' || modal.mode === 'edit') && (
              <>
                <div className="form-field">
                  <label className="form-label">Name *</label>
                  <input type="text" className="form-input" value={form.name}
                    onChange={e => setForm({...form, name: e.target.value})}
                    placeholder={isTermMode ? 'e.g. Test, Oral/Viva, Class Work…' : 'e.g. Day to Day, EOT, Oral Work…'} autoFocus />
                </div>
                <div className="form-field">
                  <label className="form-label">Order (column position in grade report)</label>
                  <input type="number" className="form-input" value={form.order} min="0"
                    onChange={e => setForm({...form, order: e.target.value})} />
                </div>
              </>
            )}

            <div className="form-field">
              <label className="form-label">Note for admin</label>
              <input type="text" className="form-input" required value={note}
                onChange={e => setNote(e.target.value)}
                placeholder="Reason for this request…" />
            </div>

            <div style={{background:'#fff7ed',border:'1px solid #fed7aa',borderRadius:9,padding:'9px 12px',fontSize:'.76rem',color:'#c2410c',marginBottom:4}}>
              📋 This request will be sent to admin for approval.
            </div>

            <div className="modal-btns">
              <button className="btn btn-ghost" onClick={() => setModal(null)}>Cancel</button>
              <button className="btn btn-orange" onClick={submit} disabled={saving}>
                <PaperAirplaneIcon />{saving ? 'Submitting…' : 'Submit Request'}
              </button>
            </div>
          </div>
        </div>
      
      , document.body)}
    </div>
  );
};
export default CoordAssessmentTypes;